/**
 * Project context types and validation.
 *
 * Defines structured context metadata for projects:
 * - phase: active (full analysis) or historical (retrospective)
 * - focus_area: construction type determining analysis scope
 * - notes: optional freeform text
 *
 * Values must match database CHECK constraints exactly.
 * See:
 * - supabase/migrations/20260129100000_add_project_context_to_projects.sql
 * - supabase/migrations/20260219120000_add_procurement_type_check_to_project_context.sql
 */

import { z } from "zod";
import { FocusAreaEnum, ProjectPhaseEnum } from "./supabase.ext";

// Re-export enums for convenience
export { FocusAreaEnum, ProjectPhaseEnum };

// ============================================================================
// Constants (derived from enums, must match database CHECK constraints)
// ============================================================================

/**
 * Project phases.
 * - active: Full analysis mode (risks, deadlines, issues)
 * - historical: Retrospective mode (patterns, lessons learned)
 */
export const PROJECT_PHASES = Object.values(ProjectPhaseEnum) as [string, ...string[]];

/**
 * Focus areas for construction projects.
 * Determines analysis scope and relevant categories.
 */
export const FOCUS_AREAS = Object.values(FocusAreaEnum) as [string, ...string[]];

/**
 * Maximum character length for notes field.
 * Enforced at UI, API, and database levels.
 */
export const MAX_NOTES_LENGTH = 3000;

// ============================================================================
// Zod Schemas
// ============================================================================

export const projectPhaseSchema = z.enum(PROJECT_PHASES);
export const focusAreaSchema = z.enum(FOCUS_AREAS);

export const projectContextSchema = z.object({
  phase: projectPhaseSchema,
  focus_area: focusAreaSchema,
  notes: z
    .string()
    .max(MAX_NOTES_LENGTH, `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`)
    .optional()
    .default(""),
});

// ============================================================================
// TypeScript Types (inferred from Zod)
// ============================================================================

export type ProjectPhase = z.infer<typeof projectPhaseSchema>;
export type FocusArea = z.infer<typeof focusAreaSchema>;
export type ProjectContext = z.infer<typeof projectContextSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate and parse project context.
 * Throws ZodError if validation fails.
 */
export function validateProjectContext(data: unknown): ProjectContext {
  return projectContextSchema.parse(data);
}

/**
 * Safe validation that returns success/error instead of throwing.
 * Use for form validation and API responses.
 */
export function safeValidateProjectContext(data: unknown) {
  return projectContextSchema.safeParse(data);
}
